/**
 * ScenarioFleetModal — controls the synthetic fleet that feeds the Allocation
 * Planner: total count, per-tier type mix, arrival/departure day curves, and the
 * deterministic seed. Edits write a live working copy in the store; "Apply"
 * regenerates the fleet and resets placements. Custom scenarios persist
 * (localStorage) so they survive reload.
 */
import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Dice5, RotateCcw, Save, Trash2, Zap } from 'lucide-react'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { DEMO_SCENARIOS } from '../data/scenarios'
import {
  DEFAULT_TIER_WEIGHTS,
  DEFAULT_ARRIVAL_DAY_WEIGHTS,
  DEFAULT_DEP_DAY_WEIGHTS,
} from '../data/syntheticInquirySource'
import { type Tier, TIER_ORDER, TIER_LABELS } from '../engine/tiers'
import { WINDOW_START } from '../data/eventWindow'

const DAY_MS = 24 * 60 * 60 * 1000
/** Event-window day count (matches the default arrival-curve length). */
const DAY_COUNT = DEFAULT_ARRIVAL_DAY_WEIGHTS.length

const dayLabel = (idx: number) =>
  new Date(WINDOW_START + idx * DAY_MS).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

/** Sparse [dayIdx, weight] pairs → dense array over the window. */
function densify(pairs: Array<[number, number]>): number[] {
  const out = new Array(DAY_COUNT).fill(0)
  for (const [idx, w] of pairs) if (idx >= 0 && idx < DAY_COUNT) out[idx] = w
  return out
}
/** Dense array → sparse [dayIdx, weight] pairs (drops zeros). */
function sparsify(dense: number[]): Array<[number, number]> {
  return dense.map((w, idx) => [idx, w] as [number, number]).filter(([, w]) => w > 0)
}

export function ScenarioFleetModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const activeScenarioId = useEventPlannerStore((s) => s.activeScenarioId)
  const options = useEventPlannerStore((s) => s.scenarioOptions)
  const customScenarios = useEventPlannerStore((s) => s.customScenarios)
  const setScenarioOptions = useEventPlannerStore((s) => s.setScenarioOptions)
  const selectScenario = useEventPlannerStore((s) => s.selectScenario)
  const saveCustomScenario = useEventPlannerStore((s) => s.saveCustomScenario)
  const deleteCustomScenario = useEventPlannerStore((s) => s.deleteCustomScenario)
  const regenerateFleet = useEventPlannerStore((s) => s.regenerateFleet)

  const [saveName, setSaveName] = useState('')

  const count = options.count ?? 400
  const seed = options.seed ?? 0
  const tierWeights = options.tierWeights ?? DEFAULT_TIER_WEIGHTS
  const arrival = options.arrivalDayWeights ?? DEFAULT_ARRIVAL_DAY_WEIGHTS
  const depDense = useMemo(
    () => densify(options.depDayWeights ?? DEFAULT_DEP_DAY_WEIGHTS),
    [options.depDayWeights]
  )

  const mixTotal = TIER_ORDER.reduce((s, t) => s + (tierWeights[t] ?? 0), 0) || 1

  const setTier = (t: Tier, v: number) =>
    setScenarioOptions({ tierWeights: { ...DEFAULT_TIER_WEIGHTS, ...tierWeights, [t]: Math.max(0, v) } })
  const setArrivalDay = (idx: number, v: number) => {
    const next = [...arrival]
    next[idx] = Math.max(0, v)
    setScenarioOptions({ arrivalDayWeights: next })
  }
  const setDepDay = (idx: number, v: number) => {
    const next = [...depDense]
    next[idx] = Math.max(0, v)
    setScenarioOptions({ depDayWeights: sparsify(next) })
  }

  const randomizeSeed = () => {
    // App runtime: Math.random is fine — gives a fresh draw from the same mix.
    const next = Math.floor(Math.random() * 1_000_000_000)
    setScenarioOptions({ seed: next })
  }

  const apply = () => regenerateFleet()

  const presets = [...Object.values(DEMO_SCENARIOS), ...Object.values(customScenarios)]
  const activeIsCustom = !!customScenarios[activeScenarioId]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-slate-950 text-white sm:max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogTitle className="text-base font-bold text-white">Scenario / Fleet</DialogTitle>
        <p className="-mt-1 text-[11px] text-white/40">
          Shape the synthetic fleet — count, type mix, arrival/departure timing, seed. Apply
          regenerates the fleet and clears placements.
        </p>

        {/* Preset row */}
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-white/40">Preset</span>
          <select
            value={activeScenarioId}
            onChange={(e) => selectScenario(e.target.value)}
            className="flex-1 rounded-md border border-white/10 bg-slate-900 px-2 py-1.5 text-xs text-white/90 outline-none focus:border-sky-400/50"
          >
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.custom ? ' ★' : ''}
              </option>
            ))}
          </select>
          {activeIsCustom && (
            <button
              type="button"
              onClick={() => deleteCustomScenario(activeScenarioId)}
              title="Delete this custom scenario"
              className="flex h-8 w-8 items-center justify-center rounded-md bg-white/5 text-white/50 hover:bg-red-500/20 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <Tabs defaultValue="fleet" className="mt-3">
          <TabsList className="bg-white/5">
            <TabsTrigger value="fleet">Fleet</TabsTrigger>
            <TabsTrigger value="mix">Type Mix</TabsTrigger>
            <TabsTrigger value="timing">Timing</TabsTrigger>
          </TabsList>

          {/* Fleet: count + seed */}
          <TabsContent value="fleet" className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-white/40">
                <span>Fleet count</span>
                <span className="tabular-nums text-sky-300">{count}</span>
              </div>
              <input
                type="range"
                min={50}
                max={800}
                step={10}
                value={count}
                onChange={(e) => setScenarioOptions({ count: Number(e.target.value) })}
                className="w-full accent-sky-400"
              />
              <input
                type="number"
                min={1}
                value={count}
                onChange={(e) => setScenarioOptions({ count: Math.max(1, Number(e.target.value) || 0) })}
                className="w-28 rounded border border-white/10 bg-black/40 px-2 py-1 text-xs tabular-nums text-white/90 outline-none focus:border-sky-400/50"
              />
            </div>

            <div className="space-y-1.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-white/40">Seed</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={seed}
                  onChange={(e) => setScenarioOptions({ seed: Number(e.target.value) || 0 })}
                  className="w-40 rounded border border-white/10 bg-black/40 px-2 py-1 text-xs tabular-nums text-white/90 outline-none focus:border-sky-400/50"
                />
                <button
                  type="button"
                  onClick={randomizeSeed}
                  className="flex items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1.5 text-xs font-medium text-white/80 hover:bg-white/20"
                >
                  <Dice5 className="h-3.5 w-3.5" /> Randomize
                </button>
              </div>
              <p className="text-[10px] text-white/30">
                Same seed + same settings ⇒ identical fleet. Randomize draws a different fleet from
                the same distribution.
              </p>
            </div>
          </TabsContent>

          {/* Type mix */}
          <TabsContent value="mix" className="space-y-2.5">
            <p className="text-[11px] text-white/40">
              Relative weights per size tier — shown as a live share of the fleet.
            </p>
            {TIER_ORDER.map((t) => {
              const w = tierWeights[t] ?? 0
              const pct = Math.round((w / mixTotal) * 100)
              return (
                <div key={t} className="grid grid-cols-[6rem_1fr_3.5rem_3rem] items-center gap-2">
                  <span className="text-[11px] text-white/70">{TIER_LABELS[t]}</span>
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={1}
                    value={w}
                    onChange={(e) => setTier(t, Number(e.target.value))}
                    className="w-full accent-sky-400"
                  />
                  <input
                    type="number"
                    min={0}
                    value={w}
                    onChange={(e) => setTier(t, Number(e.target.value) || 0)}
                    className="w-full rounded border border-white/10 bg-black/40 px-1.5 py-1 text-center text-[11px] tabular-nums text-white/90 outline-none focus:border-sky-400/50"
                  />
                  <span className="text-right text-[11px] tabular-nums text-sky-300">{pct}%</span>
                </div>
              )
            })}
            <button
              type="button"
              onClick={() => setScenarioOptions({ tierWeights: { ...DEFAULT_TIER_WEIGHTS } })}
              className="mt-1 flex items-center gap-1.5 text-[10px] text-white/40 hover:text-white/70"
            >
              <RotateCcw className="h-3 w-3" /> Reset to standard mix
            </button>
          </TabsContent>

          {/* Timing: arrival + departure curves */}
          <TabsContent value="timing" className="space-y-4">
            <CurveEditor
              label="Arrival ramp"
              hint="Relative arrivals per day across the event window."
              values={arrival}
              onChange={setArrivalDay}
              onReset={() => setScenarioOptions({ arrivalDayWeights: [...DEFAULT_ARRIVAL_DAY_WEIGHTS] })}
            />
            <CurveEditor
              label="Departure wall"
              hint="Relative departures per day (the Sunday exodus). Same-day quick-turns are separate."
              values={depDense}
              onChange={setDepDay}
              onReset={() =>
                setScenarioOptions({ depDayWeights: DEFAULT_DEP_DAY_WEIGHTS.map((p) => [...p] as [number, number]) })
              }
            />
          </TabsContent>
        </Tabs>

        {/* Footer: save-as + apply */}
        <div className="mt-3 flex items-center gap-2 border-t border-white/10 pt-3">
          <input
            type="text"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Save as…"
            className="w-36 rounded border border-white/10 bg-black/40 px-2 py-1.5 text-xs text-white/90 outline-none placeholder:text-white/30 focus:border-sky-400/50"
          />
          <button
            type="button"
            disabled={!saveName.trim()}
            onClick={() => {
              saveCustomScenario(saveName.trim())
              setSaveName('')
            }}
            className="flex items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1.5 text-xs font-medium text-white/80 hover:bg-white/20 disabled:opacity-40"
          >
            <Save className="h-3.5 w-3.5" /> Save
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={apply}
            className="flex items-center gap-1.5 rounded-md bg-sky-500/30 px-3 py-1.5 text-xs font-semibold text-sky-100 hover:bg-sky-500/40"
          >
            <Zap className="h-3.5 w-3.5" /> Apply &amp; regenerate
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function CurveEditor({
  label,
  hint,
  values,
  onChange,
  onReset,
}: {
  label: string
  hint: string
  values: number[]
  onChange: (idx: number, v: number) => void
  onReset: () => void
}) {
  const max = Math.max(1, ...values)
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-white/40">{label}</span>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/70"
        >
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      </div>
      <div className="flex items-end gap-1">
        {values.map((v, idx) => (
          <div key={idx} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex h-16 w-full items-end">
              <div
                className="w-full rounded-t bg-sky-400/40"
                style={{ height: `${(v / max) * 100}%` }}
                title={`${dayLabel(idx)}: ${v}`}
              />
            </div>
            <input
              type="number"
              min={0}
              value={v}
              onChange={(e) => onChange(idx, Number(e.target.value) || 0)}
              className="w-full rounded border border-white/10 bg-black/40 px-0.5 py-0.5 text-center text-[10px] tabular-nums text-white/90 outline-none focus:border-sky-400/50"
            />
            <span className="text-[8px] leading-tight text-white/30">{dayLabel(idx).split(' ')[0]}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-white/30">{hint}</p>
    </div>
  )
}
